/**
 * hata.ts
 *
 * NE İŞE YARAR : Sunucudan gelen hatanın istemci tarafındaki tek tipi
 * KAYNAK SPEC  : 04-api-sozlesmesi.md §2.6 (hata gövdesi ve kod tablosu)
 *                15-arayuz-standardi.md §9.1 (hata mesajı kuralı) · §9.2 (çakışma)
 * BAĞLI OLDUĞU : src/metin (yalnız sunucuya hiç ulaşılamadığı durumlar için)
 * KULLANAN     : src/api/istemci.ts · bütün ekranlar
 * DİKKAT       : 1) MESAJ SUNUCUDAN GELİR (`15` §9.1). `04` §9 her kısıt için kullanıcı
 *                   mesajını sunucu tarafında yazıyor; arayüz onu **olduğu gibi**
 *                   gösterir. Arayüz kendi mesajını yazsaydı iki metin ayrışır ve
 *                   kullanıcıya hangi kısıtın ne dediği ekrana göre değişirdi.
 *                2) EKRANIN MESAJI EZMESİ **İSTİSNADIR**, kural değil. Yalnız sunucunun
 *                   genel mesajı o bağlamda yanlış olduğunda yapılır ve gerekçesi
 *                   ekranda yazılır. Giriş ekranı bunun tek örneğidir: `AUTH_REQUIRED`in
 *                   *"Oturum açmanız gerekiyor."* mesajı, zaten oturum açmaya çalışan
 *                   bir kullanıcıya hiçbir şey söylemez.
 *                3) `BAGLANTI_YOK` SUNUCUDAN GELMEZ, İSTEMCİ ÜRETİR. `04` §2.6'nın kod
 *                   listesinde yoktur ve olmamalıdır: sunucuya hiç ulaşılamadığında
 *                   sunucunun bir kodu olamaz. `15` §9 bunu ayrı bir durum ekranı
 *                   sayıyor — *"yeniden dene, yazılmamış veri korunur"*.
 *                4) `correlation_id` SAKLANIR AMA ÖNE ÇIKARILMAZ. Kullanıcının bildirdiği
 *                   bir hatayı sunucu log'unda bulmanın tek yolu odur (`04` §2.5); ama
 *                   ekranın ana mesajı olmaz — kullanıcıya bir şey anlatmaz.
 * SON GÜNCELLEME: 2026-08-31 03:05:00
 */
import { metin } from '../metin';

/** `04` §2.6'daki kodlar + istemcinin kendi ürettiği iki değer (başlıktaki not 3). */
export type HataKodu =
  | 'MALFORMED_REQUEST'
  | 'AUTH_REQUIRED'
  | 'SESSION_REVOKED'
  | 'PERMISSION_DENIED'
  | 'NOT_FOUND'
  | 'GONE'
  | 'STATE_INVALID'
  | 'VERSION_CONFLICT'
  | 'CONSTRAINT_CONFLICT'
  | 'VALIDATION_FAILED'
  | 'REASON_REQUIRED'
  | 'IDEMPOTENCY_KEY_REUSED'
  | 'RATE_LIMITED'
  | 'EXPORT_QUOTA_EXCEEDED'
  | 'SERVER_ERROR'
  | 'UPSTREAM_UNAVAILABLE'
  /** Sunucuya hiç ulaşılamadı — istemci üretir (başlıktaki not 3). */
  | 'BAGLANTI_YOK'
  /** Yanıt geldi ama `04` §2.6 biçiminde değil — istemci üretir. */
  | 'COZUMLENEMEDI';

/** Sunucunun döndürdüğü hata gövdesi (`04` §2.6). */
interface HataGovdesi {
  readonly error?: {
    readonly code?: string;
    readonly message?: string;
    readonly correlation_id?: string;
    readonly details?: Readonly<Record<string, unknown>>;
  };
}

/**
 * Arayüzün tek hata tipi.
 *
 * `Error`dan türer ki `throw` edilebilsin ve yığın izi geliştirici konsolunda kalsın;
 * ekranlara giden şey yalnız `mesaj`dır.
 */
export class ApiHatasi extends Error {
  readonly kod: HataKodu;
  readonly http: number;
  readonly correlationId: string | undefined;
  readonly details: Readonly<Record<string, unknown>> | undefined;

  constructor(
    kod: HataKodu,
    mesaj: string,
    secenek?: {
      http?: number;
      correlationId?: string | undefined;
      details?: Readonly<Record<string, unknown>> | undefined;
    },
  ) {
    super(mesaj);
    this.name = 'ApiHatasi';
    this.kod = kod;
    this.http = secenek?.http ?? 0;
    this.correlationId = secenek?.correlationId;
    this.details = secenek?.details;
  }

  /** `VALIDATION_FAILED` gövdesindeki parola politikası ihlalleri (`04` AU-05). */
  get ihlaller(): readonly string[] {
    const ham = this.details?.['ihlaller'];
    return Array.isArray(ham) ? ham.filter((d): d is string => typeof d === 'string') : [];
  }
}

/** Ağ katmanı hiç cevap veremedi (başlıktaki not 3). */
export function baglantiHatasi(sebep: unknown): ApiHatasi {
  const hata = new ApiHatasi('BAGLANTI_YOK', metin('hata.baglantiYok'));
  hata.cause = sebep;
  return hata;
}

/**
 * HTTP yanıtını `ApiHatasi`ya çevirir.
 *
 * Gövde `04` §2.6 biçiminde değilse **uydurulmaz**: `COZUMLENEMEDI` ile genel mesaj
 * gösterilir. Sunucunun ne dediğini tahmin etmeye çalışmak, bir gün yanlış bir cümleyi
 * sunucuya mal etmek olurdu.
 */
export function yanittanHata(http: number, ham: unknown, correlationId: string | null): ApiHatasi {
  const govde = (ham ?? {}) as HataGovdesi;
  const kod = govde.error?.code;
  const mesaj = govde.error?.message;

  if (typeof kod !== 'string' || typeof mesaj !== 'string' || mesaj === '') {
    return new ApiHatasi('COZUMLENEMEDI', metin('hata.beklenmeyen'), {
      http,
      correlationId: correlationId ?? undefined,
    });
  }

  return new ApiHatasi(kod as HataKodu, mesaj, {
    http,
    // Gövdedeki değer başlıktakine tercih edilir: `04` §2.6 onu gövdenin parçası
    // sayıyor ve hata kaydında aranan değer odur.
    correlationId: govde.error?.correlation_id ?? correlationId ?? undefined,
    details: govde.error?.details,
  });
}
